import React from 'react';
import { X } from 'lucide-react';

/**
 * Vor einer neuen Rechnung: hat der Vorgang offene Arbeitsberichte, wählt der Nutzer die Quelle
 * („aus dem Angebot“ wie bisher oder „aus Arbeitsberichten“). Ohne offene Berichte geht es direkt
 * in den Editor. Vorauswahl über offerings.billing_mode.
 */

type OpenReport = {
  id: string;
  report_date: string;
  document_reference: string;
  employee_name: string | null;
  lines: Array<{ quantity: number; unit_price_vk: number }>;
};

type ChoiceState = {
  documentLabel: string;
  offeringId: string;
  billingMode: 'offer' | 'reports';
  reports: OpenReport[];
};

export type OpenDocumentEditor = (documentLabel: string, offeringId: string, workReportIds?: string[]) => void;

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

function formatDate(value: string): string {
  const d = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function reportValue(report: OpenReport): number {
  return report.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price_vk) || 0), 0);
}

export function useInvoiceSourceChoice(openEditor: OpenDocumentEditor) {
  const [state, setState] = React.useState<ChoiceState | null>(null);

  const requestDocumentEditor = React.useCallback(
    async (documentLabel: string, offeringId: string) => {
      if (documentLabel !== 'Rechnung') {
        openEditor(documentLabel, offeringId);
        return;
      }
      try {
        const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/work-reports`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error();
        const data = (await res.json()) as { billingMode?: string; reports?: Array<OpenReport & { status: string }> };
        const open = (data.reports ?? []).filter((r) => r.status === 'open');
        if (!open.length) {
          openEditor(documentLabel, offeringId);
          return;
        }
        setState({
          documentLabel,
          offeringId,
          billingMode: data.billingMode === 'reports' ? 'reports' : 'offer',
          reports: open,
        });
      } catch {
        // Ohne Berichtsdaten wie bisher: Rechnung aus dem Angebot.
        openEditor(documentLabel, offeringId);
      }
    },
    [openEditor],
  );

  const modal = state ? (
    <InvoiceSourceChoiceModal
      state={state}
      onClose={() => setState(null)}
      onConfirm={(workReportIds) => {
        setState(null);
        openEditor(state.documentLabel, state.offeringId, workReportIds);
      }}
    />
  ) : null;

  return { requestDocumentEditor, invoiceSourceModal: modal };
}

function InvoiceSourceChoiceModal({
  state,
  onClose,
  onConfirm,
}: {
  state: ChoiceState;
  onClose: () => void;
  onConfirm: (workReportIds?: string[]) => void;
}) {
  const [source, setSource] = React.useState<'offer' | 'reports'>(state.billingMode);
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set(state.reports.map((r) => r.id)));
  const selectedIds = state.reports.filter((r) => selected.has(r.id)).map((r) => r.id);
  const canConfirm = source === 'offer' || selectedIds.length > 0;

  const optionClass = (active: boolean) =>
    `w-full rounded-lg border px-4 py-3 text-left transition-colors ${
      active ? 'border-pipedrive-blue bg-pipedrive-blue/5' : 'border-app-border bg-app-surface hover:bg-app-hover'
    }`;

  return (
    <div
      className="fixed inset-0 z-[240] flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-source-title"
        className="flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-md border border-app-border bg-app-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-app-border bg-app-surface-muted/80 px-4 py-3">
          <h2 id="invoice-source-title" className="min-w-0 flex-1 text-base font-bold text-app-text">
            Rechnung erstellen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-app-text-subtle transition-colors hover:bg-slate-200 hover:text-app-text"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2 overflow-y-auto p-3 sm:p-4">
          <p className="pb-1 text-[13px] text-app-text-muted">Woraus soll die Rechnung entstehen?</p>

          <button type="button" onClick={() => setSource('offer')} className={optionClass(source === 'offer')}>
            <span className="block text-sm font-semibold text-app-text">Aus dem Angebot</span>
            <span className="mt-0.5 block text-[12px] text-app-text-subtle">
              Positionen und Mengen des Vorgangs, wie bisher.
            </span>
          </button>

          <div
            role="button"
            tabIndex={0}
            onClick={() => setSource('reports')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setSource('reports');
            }}
            className={optionClass(source === 'reports')}
          >
            <span className="block text-sm font-semibold text-app-text">Aus Arbeitsberichten</span>
            <span className="mt-0.5 block text-[12px] text-app-text-subtle">
              Gemeldete Mengen der noch nicht abgerechneten Berichte. Das Angebot bleibt unverändert.
            </span>
            {source === 'reports' ? (
              <ul className="mt-2 divide-y divide-app-border border-t border-app-border">
                {state.reports.map((report) => (
                  <li key={report.id}>
                    <label className="flex cursor-pointer items-center gap-2.5 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(report.id)}
                        onChange={(e) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(report.id);
                            else next.delete(report.id);
                            return next;
                          })
                        }
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] text-app-text">
                          {formatDate(report.report_date)}
                          {report.employee_name ? ` · ${report.employee_name}` : ''}
                        </span>
                        <span className="block text-[12px] text-app-text-subtle">
                          {[report.document_reference, `${report.lines.length} Positionen`].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span className="shrink-0 text-[13px] tabular-nums text-app-text">{eur.format(reportValue(report))}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-app-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-app-border px-3 py-1.5 text-sm font-semibold text-app-text hover:bg-app-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(source === 'reports' ? selectedIds : undefined)}
            className="rounded-lg bg-pipedrive-blue px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
}
